import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Checkbox, FormControl, FormLabel, Grid, GridItem, Heading, Input, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Radio, RadioGroup, Select, Stack, Text } from '@chakra-ui/react'
import { getCabinetPricingConfig } from 'api'
import { CABINET_FACES, CABINET_FACE_TYPES, CABINET_STYLES, applyNoEmcFabricationMultiplier, parseStructureComponents, STACK_MODES, STRUCTURE_COMPONENT_TYPES } from 'lib'
import { ExtraStructureComponents } from './ExtraStructureComponents'
import { StackModeField } from './StackModeField'

export const EdgeCabinetForm = ({ form, setForm, ...rest }) => {
  const [cabinetConfig, setCabinetConfig] = useState({
    cabinetSingleBase: 0,
    cabinetSinglePerSqft: 0,
    cabinetDoubleBase: 0,
    cabinetDoublePerSqft: 0
  })

  useEffect(() => {
    const load = async () => {
      const res = await getCabinetPricingConfig()
      setCabinetConfig(res)
    }
    load()
  }, [])

  useEffect(() => {
    let cabinetCost = 0
    if (form.cabinetHeightIn > 0 && form.cabinetLengthIn > 0) {
      let baseCost = 0
      let perSqftCost = 0
      let bonus = 0
      const sqft = form.cabinetHeightIn * form.cabinetLengthIn / 12 / 12

      switch (form.cabinetFace) {
        case CABINET_FACES.SF:
          baseCost = cabinetConfig.cabinetSingleBase
          perSqftCost = cabinetConfig.cabinetSinglePerSqft
          if (sqft >= 0 && sqft < 5) {
            bonus = 100
          } else if (sqft >= 5 && sqft < 10) {
            bonus = 150
          } else if (sqft >= 10 && sqft < 15) {
            bonus = 162
          } else if (sqft >= 15 && sqft < 20) {
            bonus = 175
          } else if (sqft >= 20 && sqft < 25) {
            bonus = 200
          } else if (sqft >= 25 && sqft < 30) {
            bonus = 225
          } else if (sqft >= 30 && sqft < 35) {
            bonus = 250
          } else if (sqft >= 35 && sqft < 40) {
            bonus = 275
          } else if (sqft >= 40 && sqft < 45) {
            bonus = 300
          } else if (sqft >= 45 && sqft < 50) {
            bonus = 325
          } else {
            bonus = 350
          }
          break
        case CABINET_FACES.DF:
          baseCost = cabinetConfig.cabinetDoubleBase
          perSqftCost = cabinetConfig.cabinetDoublePerSqft
          if (sqft >= 0 && sqft < 5) {
            bonus = 125
          } else if (sqft >= 5 && sqft < 10) {
            bonus = 150
          } else if (sqft >= 10 && sqft < 15) {
            bonus = 175
          } else if (sqft >= 15 && sqft < 20) {
            bonus = 200
          } else if (sqft >= 20 && sqft < 25) {
            bonus = 250
          } else if (sqft >= 25 && sqft < 30) {
            bonus = 300
          } else if (sqft >= 30 && sqft < 35) {
            bonus = 350
          } else if (sqft >= 35 && sqft < 40) {
            bonus = 400
          } else if (sqft >= 40 && sqft < 45) {
            bonus = 400
          } else if (sqft >= 45 && sqft < 50) {
            bonus = 425
          } else {
            bonus = 450
          }
          break
      }

      switch (form.cabinetStyle) {
        case CABINET_STYLES.XPS.value:
          cabinetCost += baseCost + sqft * perSqftCost + bonus
          break
        case CABINET_STYLES.FULL.value:
          cabinetCost += (baseCost + sqft * perSqftCost + bonus) * 1.2
          break
        case CABINET_STYLES.CUSTOM_SHAPE.value:
          // Custom Shape: 44% beyond XPS pricing, or 20% beyond Full Depth pricing
          // Since both result in the same multiplier (1.44), we use that
          cabinetCost += (baseCost + sqft * perSqftCost + bonus) * 1.44
          break
      }
      const faceTypeEntry = Object.values(CABINET_FACE_TYPES).find(t => t.value === form.cabinetFaceType) || CABINET_FACE_TYPES.Polycarbonate
      cabinetCost *= faceTypeEntry.multiplier
    }

    if (form.cphra === false && form.plpr === true) {
      cabinetCost += 45
    }
    cabinetCost = applyNoEmcFabricationMultiplier(cabinetCost, form.noEmc)
    setForm(draft => { draft.cabinetCost = parseFloat(cabinetCost.toFixed(2)) })
  }, [form.cabinetHeightIn, form.cabinetLengthIn, form.cabinetFace, form.cabinetStyle, form.cabinetFaceType, form.cphra, form.plpr, form.noEmc, cabinetConfig])

  return (
    <Box {...rest}>
      <Grid
        templateColumns="5fr 1fr"
        rowGap={5}
        columnGap={10}
        w="900px"
        maxW="100%"
      >
        <GridItem>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading size="md">Lightbox / ID Cabinet</Heading>
            <Checkbox
              borderColor="gray.400"
              isChecked={form.noCabinet}
              onChange={(e) => setForm(draft => {
                draft.noCabinet = e.target.checked
                if (draft.noCabinet) {
                  draft.cabinetHeightIn = 0
                  draft.cabinetLengthIn = 0
                  draft.cabinetFace = ''
                  draft.cabinetStyle = ''
                  draft.cabinetFaceType = CABINET_FACE_TYPES.Polycarbonate.value
                  draft.cphra = false
                  draft.plpr = false
                  draft.cabinetCost = 0
                  draft.cabinetStackMode = STACK_MODES.Stacked
                  draft.structureComponents = parseStructureComponents(draft.structureComponents).filter(item => item.type !== STRUCTURE_COMPONENT_TYPES.Cabinet)
                }
              })}
            >
              No Cabinet
            </Checkbox>
          </Box>
        </GridItem>
        <GridItem></GridItem>
        {!form.noCabinet && (
          <>
            <GridItem>
              <Grid
                templateColumns="repeat(4, 1fr)"
                gap={4}
              >
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Height (in)</FormLabel>
                    <NumberInput
                      onChange={(v) => setForm(draft => { draft.cabinetHeightIn = parseInt(v) })}
                      value={form.cabinetHeightIn}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Length (in)</FormLabel>
                    <NumberInput
                      onChange={(v) => setForm(draft => { draft.cabinetLengthIn = parseInt(v) })}
                      value={form.cabinetLengthIn}
                      min={0}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Configuration</FormLabel>
                    <Select
                      value={form.cabinetFace}
                      onChange={e => setForm(draft => { draft.cabinetFace = e.target.value })}
                      placeholder="Select"
                    >
                      <option value={CABINET_FACES.SF}>{CABINET_FACES.SF}</option>
                      <option value={CABINET_FACES.DF}>{CABINET_FACES.DF}</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Cabinet Style</FormLabel>
                    <Select
                      value={form.cabinetStyle}
                      onChange={e => setForm(draft => { draft.cabinetStyle = e.target.value })}
                      placeholder="Select"
                    >
                      <option value={CABINET_STYLES.XPS.value}>{CABINET_STYLES.XPS.label}</option>
                      <option value={CABINET_STYLES.FULL.value}>{CABINET_STYLES.FULL.label}</option>
                      <option value={CABINET_STYLES.CUSTOM_SHAPE.value}>{CABINET_STYLES.CUSTOM_SHAPE.label}</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Face Type</FormLabel>
                    <Select
                      value={form.cabinetFaceType || CABINET_FACE_TYPES.Polycarbonate.value}
                      onChange={e => setForm(draft => { draft.cabinetFaceType = e.target.value })}
                      placeholder="Select"
                    >
                      {Object.values(CABINET_FACE_TYPES).map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={4}>
                  <Text fontSize="sm">{`Current Selection: ${parseInt(form.cabinetHeightIn / 12)}' - ${form.cabinetHeightIn % 12}" * ${parseInt(form.cabinetLengthIn / 12)}' - ${form.cabinetLengthIn % 12}"`}</Text>
                </GridItem>
                <GridItem colSpan={4}>
                  <StackModeField
                    value={form.cabinetStackMode || STACK_MODES.Stacked}
                    onChange={(v) => setForm(draft => { draft.cabinetStackMode = v })}
                    label="Structure Position"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Cabinet height is entered in inches and converted to feet for overall height. Side-by-side cabinets are excluded from height.
                  </Text>
                </GridItem>
                <GridItem colSpan={4}>
                  <FormControl isRequired>
                    <RadioGroup
                      onChange={(v) => {
                        if (v === 'cphra') {
                          setForm(draft => {
                            draft.cphra = true
                            draft.plpr = false
                          })
                        }
                        if (v === 'plpr') {
                          setForm(draft => {
                            draft.cphra = false
                            draft.plpr = true
                          })
                        }
                      }}
                      value={(form.cphra === true && form.plpr === false) ? 'cphra' : ((form.cphra === false && form.plpr === true) ? 'plpr' : '')}
                    >
                      <Stack direction='column'>
                        <Radio size="sm" value='cphra'>Client providing high resolution/vectorized artwork</Radio>
                        <Radio size="sm" value='plpr'>Professional logo package - high resolution/vectorized</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                </GridItem>
              </Grid>
              <ExtraStructureComponents form={form} setForm={setForm} type={STRUCTURE_COMPONENT_TYPES.Cabinet} />
            </GridItem>
            <GridItem display="flex" alignItems="flex-end">
              <FormControl>
                <FormLabel>Cabinet Total</FormLabel>
                <Input name="cabinet-total" value={form.cabinetCost} readOnly />
              </FormControl>
            </GridItem>
            </>
        )}
      </Grid>
    </Box>
  )
}

EdgeCabinetForm.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}
